import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Grid,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  passosDoImpacto,
  PassoImpactoTipologias,
  PassoImpactoSistemistas,
} from "./AvisosImpactoN1";
import { IImpactoN1 } from "../../services/materiaisN1";

interface ModalConfirmacaoConcluirHomologacaoProps {
  open: boolean;
  onClose: () => void;
  onConfirmar: () => Promise<void>;
  quantidade: number;
  codigosProdutos: string[];
  isLoading?: boolean;
  /** Etapa D (itens 3 e 4): avisos de impacto exibidos acima da lista. */
  impacto?: IImpactoN1 | null;
}

export const ModalConfirmacaoConcluirHomologacao: React.FC<ModalConfirmacaoConcluirHomologacaoProps> = ({
  open,
  onClose,
  onConfirmar,
  quantidade,
  codigosProdutos,
  isLoading = false,
  impacto,
}) => {
  // Assistente (itens A4-A6, 02/09): passos de aviso primeiro; a confirmação é o último passo.
  const passos = passosDoImpacto(impacto);
  const [indicePasso, setIndicePasso] = useState(0);
  useEffect(() => {
    if (open) setIndicePasso(0);
  }, [open]);
  const passoAtual = indicePasso < passos.length ? passos[indicePasso] : "confirmacao";

  const handleConfirmar = async () => {
    await onConfirmar();
    onClose();
  };

  // Limitar a 5 códigos, mostrar "e mais X..." se houver mais
  // FASE 05 — 2ª validação/PO 2026-07-24 (6.2): apresentar a lista completa (rolável).
  const codigosExibidos = codigosProdutos;
  const restantes = 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          // Itens A4-A6: largura para a tabela de avisos não rolar na horizontal.
          maxWidth: passoAtual === "confirmacao" ? "400px" : "900px",
        },
      }}
    >
      <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600, pb: 1 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6">Concluir homologação</Typography>
          </Grid>
          <Grid item>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ color: "#3C90AD" }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      {passoAtual === "tipologias" && (
        <PassoImpactoTipologias
          impacto={impacto!}
          onCancelar={onClose}
          onContinuar={() => setIndicePasso((p) => p + 1)}
        />
      )}

      {passoAtual === "sistemistas" && (
        <PassoImpactoSistemistas
          impacto={impacto!}
          onCancelar={onClose}
          onContinuar={() => setIndicePasso((p) => p + 1)}
        />
      )}

      {passoAtual === "confirmacao" && (
      <>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {/* Texto literal do reporte de 02/09 (passo 3 do modal Concluir Homologação). */}
          Os produtos com status Em Homologação ficarão com status Homologado.
          <strong> Deseja continuar?</strong> ({quantidade} produto(s))
        </DialogContentText>

        {codigosExibidos.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Produtos:
            </Typography>
            <List dense sx={{ maxHeight: 150, overflow: "auto", backgroundColor: "#f5f5f5", borderRadius: 1 }}>
              {codigosExibidos.map((codigo, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemText primary={codigo} primaryTypographyProps={{ variant: "body2" }} />
                </ListItem>
              ))}
              {restantes > 0 && (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={`e mais ${restantes} produto(s)...`}
                    primaryTypographyProps={{ variant: "body2", fontStyle: "italic", color: "text.secondary" }}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          Esta ação moverá os produtos para "Homologado".
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}>
          CANCELAR
        </Button>
        <Button
          onClick={handleConfirmar}
          variant="contained"
          disabled={isLoading}
          sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
        >
          {isLoading ? "CONCLUINDO..." : "CONCLUIR"}
        </Button>
      </DialogActions>
      </>
      )}
    </Dialog>
  );
};
